import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import authFetch from '../utils/authFetch';
import PDFViewerModal from '../components/PDFViewerModal';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';
const PUBLIC_API_BASE = process.env.REACT_APP_PUBLIC_API_BASE || (isLocal ? 'http://localhost:5000' : '');
const PUBLIC_MAP_CACHE_KEY = 'public-land-map-properties-v1';
const DEFAULT_MAP_CENTER = [46.0569, 14.5058];
const STREET_TAPS_TO_UNLOCK_ALL = 5;

const getCachedPublicProperties = () => {
  try {
    const raw = sessionStorage.getItem(PUBLIC_MAP_CACHE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const createMarkerIcon = (price, isAdvertised = true) => {
  const formattedPrice = price
    ? price >= 1000000
      ? `${(price / 1000000).toFixed(1)}M`
      : price >= 1000
      ? `${(price / 1000).toFixed(0)}K`
      : String(price)
    : '—';

  const markerBackground = isAdvertised ? 'white' : '#dc2626';
  const markerBorder = isAdvertised ? '#cbd5e1' : '#991b1b';
  const markerTextColor = isAdvertised ? '#1e293b' : 'white';

  return L.divIcon({
    html: `<div style="width:auto;height:auto;min-width:48px;padding:4px 8px;border-radius:999px;background:${markerBackground};border:2px solid ${markerBorder};box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:${markerTextColor};font-weight:600;font-size:12px;white-space:nowrap;">${formattedPrice}€</div>`,
    className: '',
    iconSize: [60, 28],
    iconAnchor: [30, 14]
  });
};

const PropertiesLandMapPage = ({ isPublic = false }) => {
  const navigate = useNavigate();
  const initialPublicProperties = isPublic ? getCachedPublicProperties() : [];
  const [properties, setProperties] = useState(initialPublicProperties);
  const [loading, setLoading] = useState(!isPublic);
  const [error, setError] = useState('');
  const [mapStyle, setMapStyle] = useState('street');
  const [streetTapCount, setStreetTapCount] = useState(0);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerPath, setPdfViewerPath] = useState('');
  const [pdfViewerName, setPdfViewerName] = useState('');

  const tileConfig = useMemo(() => {
    if (mapStyle === 'satellite') {
      return {
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri'
      };
    }

    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    };
  }, [mapStyle]);

  useEffect(() => {
    const abortController = new AbortController();

    const loadProperties = async () => {
      try {
        setError('');
        if (!isPublic) {
          setLoading(true);
        }

        const response = isPublic
          ? await fetch(`${PUBLIC_API_BASE}/api/public/land-map`, { signal: abortController.signal })
          : await authFetch(`${API_BASE}/api/leads/selling?status=selling`);
        if (!response.ok) {
          throw new Error('Failed to load selling properties');
        }

        const data = await response.json();
        const filtered = Array.isArray(data) ? data.filter((entry) => {
          const lat = Number(entry.latitude);
          const lon = Number(entry.longitude);
          const isLand = isPublic || entry.propertyType === 'nepozidano stavbno zemljišče';
          return Number.isFinite(lat) && Number.isFinite(lon) && (lat !== 0 || lon !== 0) && isLand;
        }) : [];

        setProperties(filtered);
        if (isPublic) {
          sessionStorage.setItem(PUBLIC_MAP_CACHE_KEY, JSON.stringify(filtered));
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        console.error(err);
        setError('Could not load properties for the map.');
      } finally {
        setLoading(false);
      }
    };

    loadProperties();

    return () => {
      abortController.abort();
    };
  }, [isPublic]);

  useEffect(() => {
    if (!isPublic) {
      return undefined;
    }

    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyMargin = document.body.style.margin;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.margin = originalBodyMargin;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [isPublic]);

  const propertyMarkers = useMemo(() => properties.map((property) => ({
    ...property,
    title: `${property.katastrskaObcina || ''} ${property.idParceleStavbe || ''}`.trim() || 'Land property',
    latitude: Number(property.latitude),
    longitude: Number(property.longitude)
  })), [properties]);
  const showAllPublicListings = isPublic && streetTapCount >= STREET_TAPS_TO_UNLOCK_ALL;
  const visibleMarkers = useMemo(
    () => (isPublic && !showAllPublicListings
      ? propertyMarkers.filter((property) => property.advertisingEnabled !== false)
      : propertyMarkers),
    [isPublic, showAllPublicListings, propertyMarkers]
  );
  const mapCenter = visibleMarkers.length > 0
    ? [visibleMarkers[0].latitude, visibleMarkers[0].longitude]
    : DEFAULT_MAP_CENTER;
  const showMap = isPublic || (!loading && visibleMarkers.length > 0);

  const handleDownloadPDF = (property) => {
    if (property.presentationDocumentPath) {
      const base = isPublic ? PUBLIC_API_BASE : API_BASE;
      const pdfUrl = `${base}/api/public/documents/${property.presentationDocumentPath}`;
      setPdfViewerPath(pdfUrl);
      setPdfViewerName(property.presentationDocumentName || 'presentation.pdf');
      setPdfViewerOpen(true);
    }
  };

  const containerStyle = isPublic
    ? { position: 'fixed', inset: 0, margin: 0, padding: 0, overflow: 'hidden', backgroundColor: '#ffffff' }
    : { padding: '24px' };

  return (
    <div style={containerStyle}>
      {!isPublic ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={() => navigate('/real-estate')}
            style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', cursor: 'pointer' }}
          >
            <ArrowLeft size={16} />
          </button>
        </div>
      ) : null}

      {error ? <div style={{ color: '#dc2626', marginBottom: '16px' }}>{error}</div> : null}

      {showMap ? (
        <div style={{ position: 'relative', borderRadius: isPublic ? '0' : '16px', overflow: 'hidden', border: isPublic ? 'none' : '1px solid #e2e8f0', boxShadow: isPublic ? 'none' : '0 4px 10px rgba(15, 23, 42, 0.06)' }}>
          <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 1000, display: 'flex', border: '1px solid #cbd5e1', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
            <button
              type="button"
              onClick={() => {
                setMapStyle('street');
                if (isPublic) {
                  setStreetTapCount((current) => current + 1);
                }
              }}
              style={{
                border: 'none',
                borderRight: '1px solid #cbd5e1',
                backgroundColor: mapStyle === 'street' ? '#e2e8f0' : '#ffffff',
                color: '#0f172a',
                fontWeight: 600,
                padding: '8px 12px',
                cursor: 'pointer'
              }}
            >
              Street
            </button>
            <button
              type="button"
              onClick={() => setMapStyle('satellite')}
              style={{
                border: 'none',
                backgroundColor: mapStyle === 'satellite' ? '#e2e8f0' : '#ffffff',
                color: '#0f172a',
                fontWeight: 600,
                padding: '8px 12px',
                cursor: 'pointer'
              }}
            >
              Satellite
            </button>
          </div>
          {(!loading && visibleMarkers.length === 0) ? (
            <div style={{ position: 'absolute', top: '56px', left: '12px', zIndex: 1000, backgroundColor: 'rgba(255, 255, 255, 0.92)', color: '#334155', borderRadius: '10px', padding: '8px 12px', border: '1px solid #e2e8f0' }}>
              No land properties with coordinates are available yet.
            </div>
          ) : null}
          <MapContainer center={mapCenter} zoom={8} style={{ height: isPublic ? '100vh' : '640px', width: '100%' }}>
            <TileLayer attribution={tileConfig.attribution} url={tileConfig.url} />
            {visibleMarkers.map((property) => (
              <Marker key={property.id} position={[property.latitude, property.longitude]} icon={createMarkerIcon(property.price, property.advertisingEnabled !== false)}>
                <Popup>
                  <div style={{ minWidth: '200px' }}>
                    <div style={{ display: 'grid', gap: '12px', marginBottom: '12px' }}>
                      {property.surfaceArea ? (
                        <div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Površina</div>
                          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', marginTop: '4px' }}>{property.surfaceArea} m²</div>
                        </div>
                      ) : null}
                      {property.price ? (
                        <div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cena</div>
                          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', marginTop: '4px' }}>€ {Number(property.price).toLocaleString('en-US')}</div>
                        </div>
                      ) : null}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {property.presentationDocumentPath ? (
                        <button
                          type="button"
                          onClick={() => handleDownloadPDF(property)}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: 'white', color: '#374151', padding: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, transition: 'all 0.2s' }}
                          onMouseOver={(e) => { e.target.style.backgroundColor = '#f1f5f9'; }}
                          onMouseOut={(e) => { e.target.style.backgroundColor = 'white'; }}
                        >
                          <Download size={14} />
                          Predstavitev
                        </button>
                      ) : null}
                      {property.adLink ? (
                        <a
                          href={property.adLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: 'white', color: '#374151', padding: '10px', textDecoration: 'none', fontSize: '13px', fontWeight: 500, transition: 'all 0.2s' }}
                          onMouseOver={(e) => { e.target.style.backgroundColor = '#f1f5f9'; }}
                          onMouseOut={(e) => { e.target.style.backgroundColor = 'white'; }}
                        >
                          <ExternalLink size={14} />
                          Link do oglasa
                        </a>
                      ) : null}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      ) : loading ? (
        <div style={{ color: '#64748b', padding: '0' }}>Loading properties...</div>
      ) : (
        <div style={{ color: '#64748b', padding: '0' }}>No land properties with coordinates are available yet.</div>
      )}
      
      <PDFViewerModal
        isOpen={pdfViewerOpen}
        pdfPath={pdfViewerPath}
        pdfName={pdfViewerName}
        onClose={() => setPdfViewerOpen(false)}
      />
    </div>
  );
};

export default PropertiesLandMapPage;
